import React, { useRef, useState, useEffect } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { Upload, X, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface PropertyInsuranceViewProps {
  className?: string;
  illustrationUrl?: string;
  isEditMode?: boolean;
  onIllustrationChange?: (url: string | null) => void;
}

const BENEFITS = [
  "Versicherungen vergleichen",
  "Individuelles Konzept",
  "Unterstützung im Schadensfall",
  "Regelmäßige Servicetermine",
];

export function PropertyInsuranceView({
  className,
  illustrationUrl,
  isEditMode = false,
  onIllustrationChange,
}: PropertyInsuranceViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImage, setLocalImage] = useState<string | null>(illustrationUrl || null);
  const [imageScale, setImageScale] = useState(100);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setLocalImage(illustrationUrl || null);
  }, [illustrationUrl]);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `property-insurance-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImage(publicUrl);
      onIllustrationChange?.(publicUrl);
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImage(null);
    onIllustrationChange?.(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title section */}
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Werte </span>
          <span className="text-primary">absichern</span>
        </h2>
        <p className="text-lg italic text-muted-foreground">
          Wir schützen Ihr Hab und Gut.
        </p>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Illustration */}
        <div className="col-span-12 lg:col-span-7">
          <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[350px] relative">
            {localImage ? (
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                <img
                  src={localImage}
                  alt="Sachversicherung Illustration"
                  className="object-contain transition-transform"
                  style={{
                    maxWidth: `${imageScale}%`,
                    maxHeight: `${Math.min(320, 320 * imageScale / 100)}px`
                  }}
                />
                {isEditMode && (
                  <>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-8 w-8"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="w-full max-w-[180px] mt-2">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Größe: {imageScale}%
                      </Label>
                      <Slider
                        value={[imageScale]}
                        onValueChange={([v]) => setImageScale(v)}
                        min={50}
                        max={300}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full h-full">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      disabled={isUploading}
                      className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="h-8 w-8" />
                    <span className="text-sm">Bild wird hier angezeigt</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Benefits */}
        <div className="col-span-12 lg:col-span-5 flex flex-col justify-center space-y-4">
          {BENEFITS.map((benefit, index) => (
            <div key={index} className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
              <span className="text-lg font-medium">{benefit}</span>
            </div>
          ))}
          
          {/* Abgesichert stamp */}
          <div className="mt-6 flex justify-center">
            <div className="border-4 border-green-500 text-green-500 font-bold text-2xl px-6 py-2 transform rotate-[-5deg] uppercase">
              Abgesichert
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
